import { useState, useEffect } from 'react';
import {
  AddressBookDto,
  AddressBookScreenPropertiesModel,
  CountryContentResponseModel,
  SaveAddressRequest,
} from '@/services/platform/api/model';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { FormField } from '@/components/ui/form-field';
import { Phone } from '@/components/ui/phone';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useClientTranslation } from '@/lib/core';
import { Switch } from '@/components/ui/switch';
import { useLocaleStore } from '@/lib/core/stores';
import { useApp } from '@/lib/core/providers/appProvider';
import { getProfileCountries } from '@/services/platform/data/profile';
import { ScreenHead } from '@/components/shared/screen/head';
import { Heading } from '@/components/ui/heading';
import { ConfirmationSheet } from '@/components/shared/sheets/confirmation';

interface ProfileAddressesNewEditProps {
  pageProperties: AddressBookScreenPropertiesModel | undefined;
  onSave: (data: SaveAddressRequest) => Promise<void>;
  onBack: () => void;
  initialValues?: AddressBookDto;
  onDelete?: () => Promise<void>;
}

export function ProfileAddressesNewEdit({
  pageProperties,
  onSave,
  onBack,
  initialValues,
  onDelete,
}: ProfileAddressesNewEditProps) {
  const t = useClientTranslation();
  const { appSettings } = useApp();
  const locale = useLocaleStore(state => state.locale);
  const [countries, setCountries] = useState<CountryContentResponseModel[]>([]);
  const [label, setLabel] = useState(initialValues?.label ?? '');
  const [firstName, setFirstName] = useState(initialValues?.firstName ?? '');
  const [lastName, setLastName] = useState(initialValues?.lastName ?? '');
  const [country, setCountry] = useState(initialValues?.country ?? '');
  const [countryCode, setCountryCode] = useState(initialValues?.countryCode ?? '');
  const [street, setStreet] = useState(initialValues?.street ?? '');
  const [additionalInfo, setAdditionalInfo] = useState(initialValues?.additionalInfo ?? '');
  const [city, setCity] = useState(initialValues?.city ?? '');
  const [postalCode, setPostalCode] = useState(initialValues?.postalCode ?? '');
  const [phoneCountryCode, setPhoneCountryCode] = useState(initialValues?.phoneCountryCode ?? '+351');
  const [phone, setPhone] = useState(initialValues?.phone ?? '');
  const [vatNumber, setVatNumber] = useState(initialValues?.vatNumber ?? '');
  const [isPreferredDelivery, setIsPreferredDelivery] = useState(initialValues?.isPreferredDelivery ?? false);
  const [isPreferredBilling, setIsPreferredBilling] = useState(initialValues?.isPreferredBilling ?? false);
  const [saving, setSaving] = useState(false);
  const [showDiscard, setShowDiscard] = useState(false);
  const [showDelete, setShowDelete] = useState(false);
  const isEditing = !!initialValues;

  useEffect(() => {
    if (!appSettings) return;
    getProfileCountries(appSettings, locale).then(items => {
      if (items) setCountries(items);
    });
  }, [appSettings, locale]);

  const isValid = !!(lastName.trim() && country.trim() && street.trim() && city.trim() && postalCode.trim());

  async function handleSave() {
    if (!isValid || saving) return;
    setSaving(true);
    try {
      await onSave({
        label: label || null,
        firstName: firstName || null,
        lastName,
        country,
        countryCode: countryCode || null,
        street,
        additionalInfo: additionalInfo || null,
        city,
        postalCode,
        phoneCountryCode: phoneCountryCode || null,
        phone: phone || null,
        vatNumber: vatNumber || null,
        isPreferredDelivery,
        isPreferredBilling,
      });
    } finally {
      setSaving(false);
    }
  }

  return (
    <>
      <div className="flex h-full flex-col">
        <div className="flex-1 overflow-y-auto px-4 pb-8">
          <ScreenHead
            title={pageProperties?.pageName ?? t('App.Labels.AddressBook')}
            onBack={() => setShowDiscard(true)}
            ariaLabel={t('App.Labels.GoBack')}
            className="px-0 pt-0! pb-6"
          />
          <Heading
            as="h2"
            className="font-pp-neue-montreal text-jncquoi-neutral-1000 mb-4 text-[20px] leading-[120%] font-semibold"
          >
            {isEditing
              ? t('App.Labels.EditAddressTitle')
              : (pageProperties?.addNewAddressModalTitle ?? t('App.Labels.AddNewAddressTitle'))}
          </Heading>

          <div className="flex flex-col gap-4">
            <FormField id="addr-label" label={t('App.Labels.AddressBookName')}>
              <Input
                id="addr-label"
                value={label}
                placeholder={t('App.Labels.AddressBookNamePlaceholder')}
                onChange={e => setLabel(e.target.value)}
              />
            </FormField>

            <FormField id="firstName" label={t('App.Labels.FirstName')}>
              <Input
                id="firstName"
                value={firstName}
                placeholder={t('App.Labels.FirstName')}
                onChange={e => setFirstName(e.target.value)}
              />
            </FormField>

            <FormField id="lastName" label={t('App.Labels.LastName')} required>
              <Input
                id="lastName"
                value={lastName}
                placeholder={t('App.Labels.LastName')}
                onChange={e => setLastName(e.target.value)}
              />
            </FormField>

            <FormField id="country" label={t('App.Labels.DestinationCountry')} required>
              <Select
                value={country}
                onValueChange={value => {
                  setCountry(value);
                  const selected = countries.find(
                    item => (item.properties?.countryName ?? item.name ?? '') === value
                  );
                  const code = selected?.properties?.countryCode;
                  if (code) setCountryCode(code);
                }}
              >
                <SelectTrigger id="country" className="font-normal normal-case">
                  <SelectValue placeholder={t('App.Labels.SelectCountry')} />
                </SelectTrigger>
                <SelectContent withoutPortal>
                  {countries.map(item => {
                    const label = item.properties?.countryName ?? item.name ?? '';
                    return (
                      <SelectItem key={item.id} value={label}>
                        {label}
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
            </FormField>

            <FormField id="street" label={t('App.Labels.Address')} required>
              <Input
                id="street"
                value={street}
                placeholder={t('App.Labels.Address')}
                onChange={e => setStreet(e.target.value)}
              />
            </FormField>

            <FormField id="additionalInfo" label={t('App.Labels.AdditionalInformation')}>
              <Input
                id="additionalInfo"
                value={additionalInfo}
                placeholder={t('App.Labels.AdditionalInformation')}
                onChange={e => setAdditionalInfo(e.target.value)}
              />
            </FormField>

            <FormField id="city" label={t('App.Labels.City')} required>
              <Input
                id="city"
                value={city}
                placeholder={t('App.Labels.City')}
                onChange={e => setCity(e.target.value)}
              />
            </FormField>

            <FormField id="postalCode" label={t('App.Labels.PostalCode')} required>
              <Input
                id="postalCode"
                value={postalCode}
                placeholder={t('App.Labels.PostalCode')}
                onChange={e => setPostalCode(e.target.value)}
              />
            </FormField>

            <FormField id="phone" label={t('App.Labels.PhoneNumber')} required>
              <Phone
                appSettings={appSettings?.properties}
                id="phone"
                countryCode={phoneCountryCode}
                value={phone}
                onCountryCodeChange={setPhoneCountryCode}
                onChange={setPhone}
                withoutPortal
              />
            </FormField>

            <FormField id="vatNumber" label={t('App.Labels.Nif')} helpText={t('App.Labels.NifHelpText')}>
              <Input
                id="vatNumber"
                value={vatNumber}
                placeholder={t('App.Labels.Nif')}
                onChange={e => setVatNumber(e.target.value)}
              />
            </FormField>

            <div className="flex flex-col gap-4 pt-2">
              <label className="flex items-start gap-2">
                <Switch
                  checked={isPreferredDelivery}
                  onCheckedChange={setIsPreferredDelivery}
                  className="mt-0.5"
                />
                <p className="font-pp-neue-montreal text-jncquoi-neutral-1000 text-[14px] tracking-[0.5px]">
                  {t('App.Labels.PreferredDeliveryAddress')}
                </p>
              </label>
              <label className="flex items-start gap-2">
                <Switch
                  checked={isPreferredBilling}
                  onCheckedChange={setIsPreferredBilling}
                  className="mt-0.5"
                />
                <p className="font-pp-neue-montreal text-jncquoi-neutral-1000 text-[14px] tracking-[0.5px]">
                  {t('App.Labels.PreferredBillingAddress')}
                </p>
              </label>
            </div>
          </div>
        </div>

        <div
          className="border-jncquoi-neutral-400 shrink-0 border-t bg-white px-4 pt-6"
          style={{ paddingBottom: 'max(env(safe-area-inset-bottom), 1.5rem)' }}
        >
          <Button
            variant="primary-solid"
            palette="dark"
            size="lg"
            className="w-full"
            disabled={!isValid || saving}
            onClick={handleSave}
          >
            {isEditing
              ? t('App.Labels.UpdateAddress')
              : (pageProperties?.addNewAddressSaveAddressBtn ?? t('App.Labels.SaveAddress'))}
          </Button>
          {isEditing && onDelete && (
            <Button
              variant="tertiary-link"
              palette="dark"
              size="sm"
              className="mt-1 w-full"
              onClick={() => setShowDelete(true)}
            >
              {t('App.Labels.DeleteAddress')}
            </Button>
          )}
        </div>
      </div>

      <ConfirmationSheet
        open={showDiscard}
        onOpenChange={setShowDiscard}
        onConfirm={async () => {}}
        onSuccess={onBack}
        headerTitle={t('App.Labels.Confirmation')}
        title={t('App.Labels.DiscardAddressTitle')}
        description={t('App.Labels.DiscardAddressDescription')}
        confirmLabel={t('App.Labels.DiscardAddress')}
        dismissLabel={t('App.Labels.ContinueEditing')}
        successText={t('App.Labels.DiscardAddressSuccess')}
      />
      {onDelete && (
        <ConfirmationSheet
          open={showDelete}
          onOpenChange={setShowDelete}
          onConfirm={onDelete}
          onSuccess={onBack}
          headerTitle={t('App.Labels.Confirmation')}
          title={t('App.Labels.DeleteAddressTitle')}
          description={t('App.Labels.DeleteAddressDescription')}
          confirmLabel={t('App.Labels.Delete')}
          dismissLabel={t('App.Labels.Cancel')}
          successText={t('App.Labels.DeleteAddressSuccess')}
        />
      )}
    </>
  );
}
